<template>
  <Flex column grow full-container>
    <Banner :alert="notification" @close="onBannerClose" />
    <Flex v-if="loading" center grow> <Spinner /> </Flex>
    <Layout
      v-if="!loading && formContainsData"
      center
      border
      overflow="hidden"
      p-2
      shrink="0"
      grow="false"
      basis="auto"
    >
      <span class="mr-3">Translate to:</span>
      <Select :items="languages" v-model="selectedLanguage" required />
    </Layout>
    <Layout
      v-if="!loading && formContainsData"
      center
      overflow="hidden"
      p-2
      shrink="0"
      grow="false"
      basis="auto"
    >
      <TranslationEntry
        :default-translation="form.defaultTranslation"
        :selected-translation="form.translations[selectedLanguage]"
        :language-id="selectedLanguage"
        :resource-key="form.resourceKey"
      >
        <Icon src="form" class="text-primary-500 mr-2 self-center" size="2xl" />
      </TranslationEntry>
    </Layout>
    <Layout
      v-if="!loading && formContainsData"
      v-show="languages.length > 0"
      column
      overflow-y-hidden
    >
      <Form v-if="form" tabs collapsible-sections :values="form">
        <FormSection
          v-for="(page, pageIndex) in filteredPages"
          :key="pageIndex"
          :title="page.defaultTranslation"
        >
          <FormSection v-if="page.defaultTranslation && !page.skipSection" title="Page Name">
            <TranslationEntry
              :default-translation="page.defaultTranslation"
              :selected-translation="page.translations[selectedLanguage]"
              :language-id="selectedLanguage"
              :resource-key="page.resourceKey"
            >
            </TranslationEntry>
          </FormSection>
          <Layout column>
            <TranslationEntry
              v-for="(topField, topFieldIndex) in page.fields"
              :key="topFieldIndex"
              :default-translation="topField.defaultTranslation"
              :selected-translation="topField.translations[selectedLanguage]"
              :language-id="selectedLanguage"
              :resource-key="topField.resourceKey"
            >
            </TranslationEntry>
          </Layout>
          <FormSection
            v-for="(section, sectionIndex) in page.sections"
            :key="sectionIndex"
            :title="section.name"
          >
            <Layout column>
              <TranslationEntry
                v-for="(field, fieldIndex) in section.fields"
                :key="fieldIndex"
                :default-translation="field.defaultTranslation"
                :selected-translation="field.translations[selectedLanguage]"
                :language-id="selectedLanguage"
                :resource-key="field.resourceKey"
              >
              </TranslationEntry>
            </Layout>
          </FormSection>
        </FormSection>
      </Form>
    </Layout>
    <Flex justify-center pb-3 v-if="!inIframe">
      <Button v-on:click="translate">Translate</Button>
    </Flex>
  </Flex>
</template>

<script>
import Layout from '@wizer/core/components/layout/Layout.vue'
import Flex from '@wizer/core/components/flex/Flex.vue'
import Form from '@wizer/core/components/form/Form.vue'
import Icon from '@wizer/core/components/icon/Icon.vue'
import FormSection from '@wizer/core/components/form/FormSection.vue'
import Select from '@wizer/core/components/select/Select'
import Spinner from '@wizer/core/components/spinner/Spinner'
import { mapActions, mapState, mapGetters } from 'vuex'
import { isEmpty } from 'lodash'
import FormTranslationService from '../services/FormTranslationService'
import IframeMixin from '../mixins/IframeMixin'
import Banner from '../components/Banner'
import TranslationEntry from '../components/i18nForm/TranslationEntry'

export default {
  name: 'i18nForm',
  mixins: [IframeMixin],

  components: {
    TranslationEntry,
    Banner,
    Layout,
    Flex,
    Form,
    Icon,
    FormSection,
    Select,
    Spinner,
  },

  data() {
    return {
      loading: true,
      languages: [],
      form: null,
      selectedLanguage: null,
    }
  },

  async mounted() {
    await this.loadData()
    this.resetLanguageSelection()
    this.registerProcessCallback(this.translate)
  },

  methods: {
    async translate() {
      let notification
      try {
        if (!this.allTranslations.length) {
          notification = {
            type: 'warning',
            message: 'Nothing has been translated',
          }
          return
        }
        await FormTranslationService.post(this.allTranslations)
        await this.$store.dispatch('FormTranslation/clear', { root: true })
        await this.loadData()
        notification = {
          type: 'success',
          message: 'Translations have been submitted successfully',
          duration: this.notificationDuration,
        }
      } catch (error) {
        notification = { type: 'error', message: error.response.data }
      } finally {
        const content = window.parent
        content.postMessage(
          {
            status: 'vue-success',
            keepDialog: true,
          },
          window.location.origin,
        )
        await this.$store.dispatch('Notification/add', notification, { root: true })
      }
    },
    async loadData() {
      try {
        const formTranslationResponse = await FormTranslationService.get(this.$route.params.id)
        this.languages = formTranslationResponse.data.languages
        this.form = formTranslationResponse.data.form
      } catch (error) {
        const message = error.response ? error.response.data : error
        const notification = { type: 'error', message }
        await this.$store.dispatch('Notification/add', notification, { root: true })
      } finally {
        this.loading = false
      }
    },
    resetLanguageSelection() {
      this.selectedLanguage = this.languages[0].data.value
    },
    onBannerClose() {
      this.removeNotification(this.notifications.slice(-1)[0])
    },
    ...mapActions({ removeNotification: 'Notification/remove' }),
  },

  computed: {
    formContainsData() {
      return !isEmpty(this.form)
    },
    inIframe() {
      try {
        return window.self !== window.top
      } catch (e) {
        return true
      }
    },
    ...mapGetters('FormTranslation', ['allTranslations']),
    filteredPages() {
      return this.form.pages.filter(
        (page) =>
          (page.defaultTranslation && !page.skipSection) ||
          (page.skipSection && page.fields.length),
      )
    },
    notification() {
      if (!this.notifications.length) return null
      return this.notifications.slice(-1)[0]
    },
    ...mapState({
      notifications: (state) => state.Notification.notifications,
      notificationDuration: (state) => state.Notification.notificationDuration,
    }),
  },
}
</script>

<style lang="postcss">
.w-form__footer,
.w-form__header {
  @apply hidden;
}
.w-tabs {
  @apply overflow-x-auto;
}
.w-tab {
  flex: 0 0 auto;
}
.full-container {
  width: 100%;
}
.default-value {
  @apply mr-5;
}
</style>
